import { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MasterLayout, ProtectedRoute } from './components';
import { FronteggProvider } from '@frontegg/react';
import { useDispatch } from 'react-redux';
import { settingsOrgFlag } from './store/actions/appointment.action';
import { useSelector } from 'react-redux';
import {
  getFHIRPublicRoutes,
  getFHIRRoutes,
  getEHRRoutes,
  getEPICRoutes,
} from './modules/EHR/Routes';
import { ReferralSignup } from './components/pageComponents/SignUp';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RUM, logger } from './services';
import { antdTheme } from '@insight-health-ai/roseate/theme/antd';
import { RootProvider } from '@insight-health-ai/roseate/components';
import { ToastContainer } from '@insight-health-ai/roseate/components';
import { ConfigProvider, notification, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const queryClient = new QueryClient();

const contextOptions = {
  baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
  clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID,
};

const authOptions = {
  keepSessionAlive: true,
  disableSilentRefresh: !!window.__isPlaywright__?.(),
};

RUM.init();
logger.init();

const updateOrganization = () => {
  const organization = new URLSearchParams(location.search).get('organization');
  if (organization) {
    console.log({ organization });
    localStorage.setItem('FRONTEGG_ORGANIZATION', organization);
  }
};

const App = () => {
  const dispatch = useDispatch();
  const { authenticationStatus } = useSelector((state) => state?.authState);

  useEffect(() => {
    if (authenticationStatus) {
      dispatch(settingsOrgFlag());
    }
  }, [authenticationStatus, dispatch]);

  const getCurrentVersion = () => {
    const metaTag = document.querySelector('meta[name="app-version"]');
    return metaTag?.getAttribute('content') || null;
  };

  const checkForUpdates = async () => {
    try {
      const currentVersion = getCurrentVersion();
      if (!currentVersion) {
        console.log('No version found in current page');
        return;
      }

      const response = await fetch(window.location.origin, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      const html = await response.text();

      // Extract version from the fetched HTML
      const serverVersionMatch = html.match(
        /name="app-version"\s+content="([^"]+)"/
      );
      const serverVersion = serverVersionMatch?.[1];

      if (serverVersion && currentVersion !== serverVersion) {
        console.log(`Version mismatch`);
        showReloadNotification();
      }
    } catch (error) {
      console.log('Version check failed:', error);
    }
  };

  const showReloadNotification = () => {
    const key = 'app-update-notification';
    notification.open({
      key,
      message: 'New Version Available',
      description:
        'A new version of the application is available. Please refresh to get the latest updates.',
      duration: 0,
      placement: 'bottomRight',
      icon: <InfoCircleOutlined style={{ color: '#00d090' }} />,
      btn: (
        <Button
          type="primary"
          size="small"
          className="notification-primary-btn p-3"
          onClick={() => {
            window.location.reload();
          }}
        >
          Refresh Now
        </Button>
      ),
    });
  };

  useEffect(() => {
    updateOrganization();
  }, []);

  // Initialize version checking
  function initVersionCheck() {
    // Check immediately on page load
    setTimeout(checkForUpdates, 2000);

    // Check every 5 minutes
    setInterval(checkForUpdates, 5 * 60 * 1000);

    // Also check when user comes back to the tab
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(checkForUpdates, 1000);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVersionCheck);
  } else {
    initVersionCheck();
  }

  return (
    <BrowserRouter>
      <FronteggProvider
        contextOptions={contextOptions}
        hostedLoginBox={true}
        authOptions={authOptions}
        entitlementsOptions={{
          enabled: true,
        }}
      >
        <ConfigProvider theme={antdTheme}>
          <RootProvider>
            <ToastContainer />
            <Suspense
              fallback={
                <div
                  className="fallback d-flex align-items-center justify-content-center vw-100 vh-100"
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              }
            >
              <QueryClientProvider client={queryClient}>
                <Routes>
                  <Route element={<ProtectedRoute />}>
                    <Route path="/*" element={<MasterLayout />} />

                    {/* Protected routes that do not require MasterLayout to be rendered */}
                    {getFHIRRoutes()}
                    {getEHRRoutes()}
                  </Route>
                  {/* Public (non-authenticated) routes */}
                  {getFHIRPublicRoutes()}
                  {getEPICRoutes()}
                  <Route path="/referral-signup" element={<ReferralSignup />} />
                  <Route path="*" element={<h1>404 Page not found</h1>} />;
                </Routes>
              </QueryClientProvider>
            </Suspense>
          </RootProvider>
        </ConfigProvider>
      </FronteggProvider>
    </BrowserRouter>
  );
};

export default App;
